export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  LOGIN: '/login',
  SIGNUP: '/signup',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  PRICING: '/pricing',
  FAQ: '/faq',
  CONTACT: '/contact',
  PRIVACY: '/privacy',
  EXAMS: '/exams',
  TESTS: '/exams',
  SETTINGS: '/settings',
  SETTINGS_SECURITY: '/settings/security',
} as const

export const API_ENDPOINTS = {
  AUTH: {
    GET_SESSION: '/api/auth/get-session', // Local BFF
    REFRESH_TOKEN: '/api/auth/refresh-token', // Local BFF (Better-Auth handles this)
  },
  EXERCISES: {
    QUESTIONS: '/api/exercises/questions/info',
    TESTS: '/api/web/exercises/exercise-packs/tests',
    USER_ANSWER: '/api/exercises/answers/user-answer',
    RESULT: '/api/exercises/answers/user-answer/result',
    EXERCISE_RESULT: '/api/exercises/answers/user-answer/result/exercise',
    TEST_GRADING: '/api/exercises/answers/tests/grading',
    TEST_HISTORY: '/api/web/exercises/exercise-packs/tests/history',
    TEST_DASHBOARD: '/api/web/exercises/exercise-packs/tests/dashboard',
    TEST_START: '/api/web/exercises/exercise-packs/tests/start',
    TEST_QUESTIONS: '/api/web/exercises/exercise-packs/tests/questions',
    TEST_SESSION: '/api/web/exercises/exercise-packs/tests/session',
    TEST_RESULT: '/api/web/exercises/exercise-packs/tests/result',
    RE_GRADING: '/api/exercises/answers/tests/re-grading',
    MEMBER_LEVEL: '/api/exercises/member-level',
    UPLOAD_RECORD: '/api/exercises/upload-record-answer',
    DICTIONARY_USER: '/api/exercises/dictionary/user',
  },
  FILES: {
    UPLOAD: '/api/files/',
    PRESIGN_STREAM: '/api/files/presign/stream',
    DOWNLOAD: '/api/files/download',
  },
  AI: {
    GRADING_TTS: '/api/ai/grading/speaking/tts',
  },
} as const

export const API_PREFIXES = {
  EXERCISE: '/api/exercises',
  EXERCISE_PACK: '/api/web/exercises',
  FILE: '/api/files',
  AI: '/api/ai',
  AUTH: '/api/auth',
  INTERNAL: '/api/internal/',
} as const

export const APP_CONFIG = {
  PREPARE_TIME: 60,
  TIMER_WARNING_THRESHOLD: 60,
  PAGINATION_LIMIT: 10,
  UPLOAD_TIMEOUT: 60000,
  AUTH_RETRY_LIMIT: 3,
  MIN_WORDS_LIMIT: 150,
  SECONDS_IN_MINUTE: 60,
  MILLISECONDS_IN_SECOND: 1000,
  EXAM_MAX_SCORE: 9,
  SPECIAL_CHARS: ['é', 'à', 'è', 'ù', 'ç', 'î', 'ô', 'ê', 'ï'],
  VALID_SKILLS: ['listening', 'reading', 'writing', 'speaking'],
  MIN_PASSWORD_LENGTH: 8,
  MAX_PASSWORD_LENGTH: 128,
  NOISE_SVG_URL: `data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E`,

  AUTH: {
    PROVIDERS: {
      GOOGLE: 'google',
      LINKEDIN: 'linkedin',
      MICROSOFT: 'microsoft',
    },
    PROMPTS: {
      SELECT_ACCOUNT_CONSENT: 'select_account consent',
      SELECT_ACCOUNT: 'select_account',
    },
    SCOPES: {
      LINKEDIN: ['openid', 'profile', 'email'],
    },
    AUTHORITIES: {
      MICROSOFT: 'https://login.microsoftonline.com',
    },
    JWT: {
      ISSUER: 'comepass',
      KEY: 'comepass',
      ALGORITHM: 'RS256',
      EXPIRES_IN: '1h',
    },
  },
} as const

export const LANDING_CONTENT = {
  HERO: {
    TITLE: 'Master French Naturally',
    SUBTITLE:
      'Immerse yourself in interactive lessons, native conversations, and real-world practice. The sleekest way to fluency.',
    CTA_PRIMARY: 'Start Learning Free',
    CTA_SECONDARY: 'Watch Demo',
  },
  HERO_SECTION: {
    BADGE: 'New: AI Speaking Coach 2.0',
    WORDS: ['Speaking', 'Writing', 'Listening', 'Reading'],
    SUBTEXT:
      'The all-in-one platform for CELPIP & TEF preparation. Practice with AI-scored exams, clear feedback, and unlimited mock tests.',
    CTA_START: 'Start Free Trial',
    CTA_EXPLORE: 'Explore Features',
  },
  BENTO: {
    SPEAKING: {
      TITLE: 'AI Speaking Coach',
      DESCRIPTION:
        'Real-time pronunciation feedback and fluency analysis using advanced speech recognition.',
      FEEDBACK: 'Great pronunciation!',
    },
    WRITING: {
      TITLE: 'Smart Writing Feedback',
      DESCRIPTION: 'Instant grammar and vocabulary corrections for your essays.',
      EXAMPLE_WRONG: 'He goed to the store',
      EXAMPLE_RIGHT: 'He went to the store',
    },
    ANALYTICS: {
      TITLE: 'Detailed Analytics',
      DESCRIPTION: 'Track your progress.',
    },
    MOCK_EXAMS: {
      TITLE: 'Mock Exams',
      DESCRIPTION: 'Real test format.',
      COUNT: '50+',
    },
  },
  CTA: {
    TITLE: 'Ready to start your French journey?',
    SUBTITLE:
      'Join 10,000+ students and start speaking French from day one. No credit card required for the free trial.',
    PRIMARY: 'Get Started for Free',
    SECONDARY: 'View Pricing',
    FOOTER: '14-day money-back guarantee • Cancel anytime',
  },
  FAQ: [
    {
      label: 'How does the AI Speaking Coach work?',
      content:
        'Our AI analyzes your speech patterns, pronunciation, and vocabulary usage in real-time. It provides instant feedback on areas for improvement and suggests better phrasing, just like a native tutor would.',
    },
    {
      label: 'Can I access the content offline?',
      content:
        'Yes! Elite plan members can download lessons and practice materials for offline use, perfect for learning on the go.',
    },
    {
      label: 'What is the Daily Streak?',
      content:
        'The Daily Streak tracks your consecutive days of learning. Consistency is key to mastering a language, and our streak system helps keep you motivated.',
    },
    {
      label: 'Is there a refund policy?',
      content:
        'We offer a 14-day money-back guarantee on all our plans. If you are not satisfied with your progress, simply contact us for a full refund.',
    },
    {
      label: 'Do you support other languages?',
      content:
        'Currently, we focus exclusively on French to ensure the highest quality of content and AI accuracy. We plan to expand to Spanish and German in late 2026.',
    },
  ],
  SOCIAL_PROOF: ['Google', 'Microsoft', 'Spotify', 'Airbnb', 'Uber'],
  STATS: [
    { label: 'Active Learners', value: '10k+', icon: 'i-lucide-users' },
    { label: 'Interactive Lessons', value: '500+', icon: 'i-lucide-book-open' },
    { label: '5-Star Ratings', value: '4.9', icon: 'i-lucide-star' },
    { label: 'Native Speakers', value: '50+', icon: 'i-lucide-mic' },
  ],
} as const

export const PLANS = [
  {
    name: 'Starter',
    price: 'Free',
    period: '',
    description: 'Perfect for getting familiar with the test format.',
    features: ['1 Full Mock Test', 'Basic Reading Practice', 'Community Access'],
    button: 'Get Started',
    color: 'gray',
    popular: false,
  },
  {
    name: 'Pro',
    price: '$29',
    period: '/month',
    description: 'The best value for serious learners.',
    features: [
      'Unlimited Mock Tests',
      'AI Speaking Analysis (10/day)',
      'Writing Corrections (5/day)',
      'Performance Analytics',
    ],
    button: 'Start 7-Day Free Trial',
    color: 'primary',
    popular: true,
  },
  {
    name: 'Elite',
    price: '$99',
    period: '/lifetime',
    description: 'One-time payment for lifetime access.',
    features: ['Lifetime Access', 'Unlimited AI Analysis', 'Priority Support', 'Offline Mode'],
    button: 'Buy Once',
    color: 'gray',
    popular: false,
  },
] as const

export const FEATURES = [
  {
    title: 'Interactive Lessons',
    description:
      'Engage with dynamic content designed to keep you motivated and learning effectively.',
    icon: 'i-lucide-monitor-play',
  },
  {
    title: 'Native Speaker Audio',
    description:
      'Perfect your accent by listening to real conversations from native French speakers.',
    icon: 'i-lucide-headphones',
  },
  {
    title: 'Progress Tracking',
    description: 'Visualize your improvement with detailed analytics and mastery charts.',
    icon: 'i-lucide-bar-chart-3',
  },
  {
    title: 'Gamified Practice',
    description: 'Earn points and badges as you master reading, writing, speaking, and listening.',
    icon: 'i-lucide-trophy',
  },
] as const

export const SEO_DEFAULTS = {
  TITLE: 'TCF Canada',
  FULL_TITLE: 'TCF Canada - Master French Naturally',
  DESCRIPTION: 'Master French naturally through immersive lessons and native conversations.',
  OG_IMAGE: '/og-default.png',
} as const

export const COOKIE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  SESSION_TOKEN: 'better-auth.session_token',
} as const
